



































































































import { AlertBanner, IncidentSummary, MonitoringFilters, TailwindTableTabs } from '@/app/components';
import { TButton } from '@/app/components/common';
import { useFeatureFlags, useQueryParams } from '@/app/composable';
import { useRouter } from '@/app/composable/router';
import { WorkflowType } from '@/app/constants';
import { ExecutionQuery, MonitoringExecutionQuery } from '@/app/types';
import { ChevronLeftIcon, ChevronRightIcon } from '@vue-hero-icons/outline';
import { FilterIcon, BellIcon } from '@vue-hero-icons/solid';
import { Ref, computed, defineComponent, ref, watch } from '@vue/composition-api';
import { ScalingSquaresSpinner as OrbitSpinner } from 'epic-spinners';
import * as R from 'ramda';
import {
    IncidentPerAnalyticsPipeline,
    MonitoringQuerySummary,
    IncidentsTimeline,
    TopFailures,
    ProblematicAssets,
    ResultTimeliness,
    ResultCompleteness,
} from '../components';
import { useBlocks, useTriggerSource } from '../composable';
import { ExecutionStatus, TriggerSource } from '../constants';

type MonitoringTable =
    | 'IncidentsPerWorkflow'
    | 'IncidentsTimeline'
    | 'TopFailures'
    | 'ProblematicAssets'
    | 'Completeness'
    | 'Timeliness';

const tables: { key: MonitoringTable; label: string; component: unknown; sortBy: { field: string; asc: boolean } }[] = [
    {
        key: 'IncidentsPerWorkflow',
        label: 'Incidents per Analytics Pipeline',
        component: IncidentPerAnalyticsPipeline,
        sortBy: {
            field: 'failures',
            asc: false,
        },
    },
    {
        key: 'IncidentsTimeline',
        label: 'Incidents Timeline',
        component: IncidentsTimeline,
        sortBy: {
            field: 'created_at',
            asc: false,
        },
    },
    {
        key: 'TopFailures',
        label: 'Top Failures',
        component: TopFailures,
        sortBy: {
            field: 'executionsCount',
            asc: false,
        },
    },
    {
        key: 'ProblematicAssets',
        label: 'Problematic Assets',
        component: ProblematicAssets,
        sortBy: {
            field: 'created_at',
            asc: false,
        },
    },
    {
        key: 'Completeness',
        label: 'Result Completeness',
        component: ResultCompleteness,
        sortBy: {
            field: 'completeness',
            asc: true,
        },
    },
    {
        key: 'Timeliness',
        label: 'Result Timeliness',
        component: ResultTimeliness,
        sortBy: {
            field: 'timeliness',
            asc: true,
        },
    },
];

const QUERY_PARAM_SORT_BY_FIELD = 'sortBy';
const QUERY_PARAM_SORT_BY_ASC = 'asc';
const QUERY_PARAM_PAGE = 'page';
export default defineComponent({
    name: 'AnalyticsPipelineMonitoring',
    metaInfo: {
        title: 'Data Analytics Pipelines Incidents',
    },
    components: {
        TailwindTableTabs,
        ChevronLeftIcon,
        ChevronRightIcon,
        OrbitSpinner,
        TButton,
        FilterIcon,
        MonitoringQuerySummary,
        AlertBanner,
        MonitoringFilters,
        IncidentSummary,
        BellIcon,
    },
    setup(props, { root }) {
        const { flag } = useFeatureFlags();
        const isAlertsEnabled = flag('alerts');
        const router = useRouter();
        const { label: getTriggerSourceLabel } = useTriggerSource();

        const PAGE_SIZE = 30;
        const queryParams: Ref<
            Record<
                string,
                {
                    param: string;
                    default?: string;
                    isArray: boolean;
                    value?: any;
                    path?: string[];
                    set?: any;
                    permitted?: string[];
                }
            >
        > = computed(() => {
            return {
                searchText: {
                    param: 'search',
                    default: '',
                    isArray: false,
                    path: ['query', 'text'],
                },
                status: {
                    param: 'status',
                    default: availableFacets.value.status.default,
                    isArray: true,
                },
                timePeriod: {
                    param: 'period',
                    default: 'today',
                    isArray: false,
                },
                timePeriodStart: {
                    param: 'start',
                    isArray: false,
                    value: (value: any, defaultValue: any) =>
                        value === 'custom' && defaultValue ? defaultValue : null,
                    path: ['query', 'dateRange', 'start'],
                },
                timePeriodEnd: {
                    param: 'end',
                    isArray: false,
                    value: (value: any, defaultValue: any) =>
                        value === 'custom' && defaultValue ? defaultValue : null,
                    path: ['query', 'dateRange', 'end'],
                },
                ownership: {
                    param: 'ownership',
                    isArray: false,
                    default: 'me',
                },
                ...Object.keys(availableFacets.value).reduce((acc: any, param: string) => {
                    acc[param] = {
                        param,
                        isArray: true,
                        default: availableFacets.value[param].default,
                        permitted: availableFacets.value[param].permitted,
                        path: ['facets', param],
                    };
                    return acc;
                }, {}),
            };
        });
        const openFilters: Ref<boolean> = ref<boolean>(false);
        const summaryLoading: Ref<boolean> = ref<boolean>(true);
        const viewLoading: Ref<boolean> = ref<boolean>(true);
        const showResultsWarning: Ref<boolean> = ref<boolean>(false);
        const focusUpdate: Ref<Date> = ref<Date>(new Date());
        const sortOptions = computed(() => {
            return [{ key: 'created_at', label: 'Created at' }];
        });

        const facets: Ref<Record<string, { value: string; count: number; selected: boolean }>> = ref<
            Record<string, { value: string; count: number; selected: boolean }>
        >({});
        const { set, get, onChange } = useQueryParams(root, router, 'workflow:monitoring');
        const { blocksMap: blocks } = useBlocks();

        const availableFacets: Ref<
            Record<
                string,
                { label: string; visible: boolean; default: string[]; valueLabel?: any; permitted?: string[] }
            >
        > = computed(
            (): Record<
                string,
                { label: string; visible: boolean; default: string[]; valueLabel?: any; permitted?: string[] }
            > => {
                return {
                    status: {
                        label: 'Status',
                        visible: !['Completeness', 'Timeliness'].includes(currentTable.value),
                        default: [ExecutionStatus.Failed, ExecutionStatus.Skipped, ExecutionStatus.Cancelled],
                        permitted: [ExecutionStatus.Failed, ExecutionStatus.Skipped, ExecutionStatus.Cancelled],
                    },
                    failedBlockCategory: {
                        label: 'Failure on Block Category',
                        visible: !['Completeness', 'Timeliness'].includes(currentTable.value),
                        default: [],
                        valueLabel: (id: string) => {
                            const block = blocks.value[id];
                            return block ? block.category : id;
                        },
                    },
                    failedBlockId: {
                        label: 'Failure on Block',
                        visible: !['Completeness', 'Timeliness'].includes(currentTable.value),
                        default: [],
                        valueLabel: (id: string) => {
                            const block = blocks.value[id];
                            return block ? block.name : id;
                        },
                    },
                    executionFramework: {
                        label: 'Execution Framework',
                        visible: !['Completeness', 'Timeliness'].includes(currentTable.value),
                        default: [],
                    },
                    triggerSource: {
                        label: 'Execution Trigger',
                        visible: !['Completeness', 'Timeliness'].includes(currentTable.value),
                        default: [],
                        valueLabel: (triggerSourceKey: string) =>
                            getTriggerSourceLabel(triggerSourceKey as TriggerSource),
                    },
                };
            },
        );
        const currentTable: Ref<MonitoringTable> = computed({
            get: () => {
                return (root.$route.query?.view as MonitoringTable) || tables[0].key;
            },
            set: (newView: string) => {
                set('view', newView, tables[0].key);
            },
        });
        const currentComponent = computed(
            () =>
                tables.find((t: { key: string }) => t.key === currentTable.value) as {
                    key: string;
                    label: string;
                    component: any;
                    sortBy: { field: string; asc: boolean };
                },
        );

        const filters = ref<{
            query: ExecutionQuery;
            facets: Record<string, any>;
        }>();

        const sortBy = ref<{
            field: string;
            asc: boolean;
        }>({
            field: get(QUERY_PARAM_SORT_BY_FIELD, false, currentComponent.value?.sortBy.field),
            asc: get(QUERY_PARAM_SORT_BY_ASC, false, `${currentComponent.value?.sortBy.asc}`) === 'true',
        });

        const pagination = ref<{
            index: number;
            pageSize: number;
        }>({
            index: 0,
            pageSize: PAGE_SIZE,
        });

        const filterFacets = computed(() =>
            Object.keys(availableFacets.value).reduce((acc: Record<string, any>, facetKey: string) => {
                if (filters.value) acc[facetKey] = R.clone(filters.value.facets[facetKey]);
                return acc;
            }, {}),
        );

        const query: Ref<MonitoringExecutionQuery | undefined> = computed(() => {
            if (!filters.value?.query || !filterFacets.value || !sortBy.value || !pagination.value) return undefined;
            return {
                query: R.clone(filters.value.query),
                facets: ['Completeness', 'Timeliness'].includes(currentTable.value)
                    ? Object.keys(filterFacets.value).reduce((acc: Record<string, unknown[]>, key: string) => {
                          acc[key] = availableFacets.value[key].default;
                          set(
                              queryParams.value[key].param,
                              availableFacets.value[key].default,
                              availableFacets.value[key].default,
                          );
                          return acc;
                      }, {})
                    : R.clone(filterFacets.value),
                sortBy: R.clone(sortBy.value),
                pagination: R.clone(pagination.value),
            };
        });

        const loading = computed(() => summaryLoading.value && viewLoading.value);

        const currentView = computed(() => (currentComponent.value ? currentComponent.value.component : null));

        const setSummaryLoading = (l: boolean) => {
            summaryLoading.value = l;
        };

        const setViewLoading = (l: boolean) => {
            viewLoading.value = l;
        };

        const changePage = (newPage: number) => {
            set(QUERY_PARAM_PAGE, `${newPage}`, '1');
            pagination.value.index = (newPage - 1) * PAGE_SIZE;
        };

        const setFacets = (newFacets: Record<string, { value: string; count: number; selected: boolean }>) => {
            facets.value = newFacets;
        };

        const toggleFilters = () => {
            openFilters.value = !openFilters.value;
            if (openFilters.value) focusUpdate.value = new Date();
        };

        const filtersChange = (newFilters: any) => {
            filters.value = R.clone(newFilters);
        };

        const changeSort = (newSort: { field: string; asc: boolean } | null) => {
            const defaultSortField = currentComponent.value?.sortBy.field || '';
            const defaultSortAsc = currentComponent.value?.sortBy.asc ? 'true' : 'false';

            if (newSort) {
                sortBy.value = newSort;
                set(QUERY_PARAM_SORT_BY_FIELD, newSort.field, defaultSortField);
                set(QUERY_PARAM_SORT_BY_ASC, newSort.asc ? 'true' : 'false', defaultSortAsc);
            } else {
                sortBy.value = currentComponent.value.sortBy;
                set(QUERY_PARAM_SORT_BY_FIELD, defaultSortField, defaultSortField);
                set(QUERY_PARAM_SORT_BY_ASC, defaultSortAsc, defaultSortAsc);
            }
        };

        const changeView = () => {
            changeSort(null);
            changePage(1);
        };

        const updateShowResultsWarning = (value: boolean) => {
            showResultsWarning.value = value;
        };

        onChange(() => {
            sortBy.value = {
                field: get(QUERY_PARAM_SORT_BY_FIELD, false, currentComponent.value?.sortBy.field),
                asc: get(QUERY_PARAM_SORT_BY_ASC, false, `${currentComponent.value?.sortBy.asc}`) === 'true',
            };
        });

        watch(
            () => query.value,
            (newQuery: MonitoringExecutionQuery | undefined, oldQuery: MonitoringExecutionQuery | undefined) => {
                // reset page if filters changed
                if (
                    !R.isNil(oldQuery) &&
                    !R.isNil(newQuery) &&
                    !R.equals({ ...newQuery, pagination: undefined }, { ...oldQuery, pagination: undefined })
                )
                    changePage(1);
            },
        );

        changePage(isNaN(get(QUERY_PARAM_PAGE, false, '1')) ? 1 : parseInt(get(QUERY_PARAM_PAGE, false, '1'), 10));

        return {
            blocks,
            query,
            facets,
            openFilters,
            loading,
            currentTable,
            tables,
            currentView,
            focusUpdate,
            sortOptions,
            sortBy,
            showResultsWarning,
            WorkflowType,
            availableFacets,
            filterFacets,
            updateShowResultsWarning,
            setSummaryLoading,
            setViewLoading,
            changePage,
            setFacets,
            toggleFilters,
            filtersChange,
            changeSort,
            changeView,
            R,
            queryParams,
            isAlertsEnabled,
        };
    },
});
